<template>
  <section class="view-stack">
    <article class="dashboard-panel">
      <div class="panel-heading">
        <div>
          <h2>我的工单</h2>
          <p>工单服务时间：工作日 9:00–22:00，服务时段内提交的工单将在 30 分钟内回复。</p>
        </div>
        <div class="feedback-actions">
          <el-button icon="el-icon-refresh" circle plain :loading="loading" aria-label="刷新工单" @click="loadFeedback" />
          <el-button type="primary" icon="el-icon-edit-outline" @click="openCreateDialog">提工单</el-button>
        </div>
      </div>

      <div class="feedback-table-wrap">
        <el-table
          v-loading="loading"
          :data="items"
          class="dashboard-table feedback-table"
          empty-text="暂无工单"
        >
          <el-table-column label="工单内容" min-width="280">
            <template slot-scope="scope">
              <div class="feedback-summary">{{ scope.row.userContent }}</div>
              <div v-if="scope.row.adminContent" class="feedback-reply-summary">
                回复：{{ scope.row.adminContent }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120">
            <template slot-scope="scope">
              <el-tag :type="statusTagType(scope.row.status)" size="small">
                {{ statusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="提交时间" width="170">
            <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
          </el-table-column>
          <el-table-column label="回复时间" width="170">
            <template slot-scope="scope">{{ formatTime(scope.row.replyTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" @click="openDetail(scope.row)">查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="items.length" class="feedback-mobile-list">
          <article v-for="item in items" :key="item.id || item.createTime" class="feedback-mobile-card">
            <div class="feedback-mobile-card__header">
              <el-tag :type="statusTagType(item.status)" size="small">
                {{ statusLabel(item.status) }}
              </el-tag>
              <span>{{ formatTime(item.createTime) }}</span>
            </div>
            <div class="feedback-mobile-card__content">{{ item.userContent }}</div>
            <div v-if="item.adminContent" class="feedback-mobile-card__reply">
              回复：{{ item.adminContent }}
            </div>
            <div class="feedback-mobile-card__footer">
              <span>回复时间：{{ formatTime(item.replyTime) }}</span>
              <el-button type="text" @click="openDetail(item)">查看详情</el-button>
            </div>
          </article>
        </div>
        <div v-else-if="!loading" class="feedback-mobile-empty">暂无工单</div>
      </div>
    </article>

    <el-dialog title="提交工单" :visible.sync="createVisible" width="560px" append-to-body>
      <p class="feedback-create-note">请描述您遇到的问题或产品建议。每次提交会创建一条独立工单。</p>
      <el-input
        v-model="content"
        type="textarea"
        :rows="7"
        maxlength="5000"
        show-word-limit
        resize="vertical"
        placeholder="请输入问题或建议"
      />
      <span slot="footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" :disabled="!content.trim()" @click="submitFeedbackNow">提交工单</el-button>
      </span>
    </el-dialog>

    <el-dialog title="工单详情" :visible.sync="detailVisible" width="560px" append-to-body>
      <template v-if="selected">
        <div class="feedback-detail-meta">
          <el-tag :type="statusTagType(selected.status)" size="small">
            {{ statusLabel(selected.status) }}
          </el-tag>
          <span>提交于 {{ formatTime(selected.createTime) }}</span>
        </div>
        <section class="feedback-detail-block">
          <h3>工单内容</h3>
          <p>{{ selected.userContent }}</p>
        </section>
        <section v-if="selected.adminContent" class="feedback-detail-block feedback-admin-reply">
          <div class="feedback-detail-reply-heading">
            <h3>管理员回复</h3>
            <time>{{ formatTime(selected.replyTime) }}</time>
          </div>
          <p>{{ selected.adminContent }}</p>
        </section>
        <el-empty v-else :image-size="64" description="等待管理员回复" />
      </template>
      <span slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import { fetchFeedback, markFeedbackReplyRead, submitFeedback } from '../../api/feedback';

export default {
  name: 'FeedbackView',
  data() {
    return {
      items: [],
      loading: false,
      submitting: false,
      createVisible: false,
      detailVisible: false,
      content: '',
      selected: null
    };
  },
  created() {
    this.loadPage();
  },
  methods: {
    async loadPage() {
      this.loadFeedback();
    },
    async loadFeedback() {
      this.loading = true;
      try {
        const { data } = await fetchFeedback();
        if (!data || data.code !== 0) throw new Error((data && data.message) || '工单加载失败');
        this.items = Array.isArray(data.data) ? data.data : [];
      } catch (error) {
        this.$message.error(error.message || '工单加载失败');
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.content = '';
      this.createVisible = true;
    },
    async submitFeedbackNow() {
      const content = this.content.trim();
      if (!content || this.submitting) return;
      this.submitting = true;
      try {
        const { data } = await submitFeedback(content);
        if (!data || data.code !== 0) throw new Error((data && data.message) || '工单提交失败');
        if (data.data) this.items.unshift(data.data);
        else await this.loadFeedback();
        this.createVisible = false;
        this.content = '';
        this.$message.success('工单已提交');
      } catch (error) {
        this.$message.error(error.message || '工单提交失败');
      } finally {
        this.submitting = false;
      }
    },
    async openDetail(item) {
      this.selected = item;
      this.detailVisible = true;
      if (Number(item.status) !== 2) return;
      try {
        const { data } = await markFeedbackReplyRead(item.id);
        if (!data || data.code !== 0) throw new Error((data && data.message) || '工单已读状态更新失败');
        this.$set(item, 'status', 3);
        this.$root.$emit('feedback-read');
      } catch (error) {
        this.$message.error(error.message || '工单已读状态更新失败');
      }
    },
    statusLabel(status) {
      const value = Number(status);
      if (value === 1) return '待回复';
      if (value === 2) return '新回复';
      return '已读';
    },
    statusTagType(status) {
      return Number(status) === 3 ? 'success' : 'warning';
    },
    formatTime(value) {
      if (!value) return '--';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '--';
      const pad = number => String(number).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
  }
};
</script>

<style scoped>
.feedback-actions { display: flex; align-items: center; gap: 8px; }
.feedback-table-wrap { width: 100%; overflow-x: auto; }
.feedback-mobile-list,
.feedback-mobile-empty { display: none; }
.feedback-summary { display: -webkit-box; overflow: hidden; color: #263f4a; line-height: 1.55; white-space: pre-wrap; overflow-wrap: anywhere; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.feedback-reply-summary { margin-top: 5px; overflow: hidden; color: #758891; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.feedback-create-note { margin: 0 0 12px; color: #687c85; font-size: 13px; line-height: 1.6; }
.feedback-detail-meta { display: flex; align-items: center; gap: 10px; color: #81919a; font-size: 12px; }
.feedback-detail-block { margin-top: 18px; padding: 14px; border: 1px solid #e3ece8; border-radius: 6px; background: #fbfdfc; }
.feedback-detail-block h3 { margin: 0; color: #304a55; font-size: 14px; }
.feedback-detail-block p { margin: 10px 0 0; color: #405761; line-height: 1.7; white-space: pre-wrap; overflow-wrap: anywhere; }
.feedback-admin-reply { background: #f3faf7; border-color: #d6ebe2; }
.feedback-detail-reply-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.feedback-detail-reply-heading time { color: #8a9a9d; font-size: 12px; }
@media (max-width: 640px) {
  .panel-heading { align-items: flex-start; }
  .feedback-actions { flex: 0 0 auto; }
  .feedback-table { display: none; }
  .feedback-table-wrap { overflow: visible; }

  .feedback-mobile-list {
    display: grid;
    gap: 12px;
  }

  .feedback-mobile-card {
    display: grid;
    gap: 10px;
    padding: 14px;
    border: 1px solid #deebe6;
    border-radius: 8px;
    background: #ffffff;
  }

  .feedback-mobile-card__header,
  .feedback-mobile-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .feedback-mobile-card__header span,
  .feedback-mobile-card__footer span {
    color: #718590;
    font-size: 12px;
  }

  .feedback-mobile-card__content,
  .feedback-mobile-card__reply {
    color: #263f4a;
    line-height: 1.7;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .feedback-mobile-card__reply {
    padding: 9px 10px;
    color: #526b78;
    background: #f7fbfa;
    border-radius: 6px;
    font-size: 12px;
  }

  .feedback-mobile-card__footer {
    padding-top: 10px;
    border-top: 1px solid #e5efeb;
  }

  .feedback-mobile-empty {
    display: block;
    padding: 24px 12px;
    color: #748894;
    text-align: center;
  }
}
</style>
